<template>
	<div v-if="moduleStatusesDetails.length > 0" class="statuses-container">
		<template v-if="isOpen">
			<ModuleStatusItem v-for="status in importantStatuses" :key="status.key" :status="status" />

			<ModuleStatusItem v-for="status in problemStatuses" :key="status.key" :status="status" />

			<template v-if="otherStatuses.length > 0">
				<button
					type="button"
					class="other-statuses-toggle"
					data-testid="other-statuses-toggle"
					aria-label="other statuses"
					:aria-expanded="otherStatusesOpen"
					@click.stop="otherStatusesOpen = !otherStatusesOpen"
				>
					<DividerAutoDirection small class="other-statuses-divider">
						{{ $t('moduleStatuses.otherStatuses') }}
					</DividerAutoDirection>
					<DownOutlined :class="{ 'other-statuses-chevron-open': otherStatusesOpen }" />
				</button>
				<template v-if="otherStatusesOpen">
					<ModuleStatusItem v-for="status in otherStatuses" :key="status.key" :status="status" />
				</template>
			</template>
		</template>
		<div
			v-else
			class="statuses-floater mouse-pointer nobr"
			role="button"
			tabindex="0"
			aria-label="module status summary"
			@click="onClick"
			@keydown.enter="onClick"
			@keydown.space.prevent="onClick"
		>
			<Badge :status="summary" /> {{ $t('moduleStatuses.summary.' + summary) }}
		</div>
	</div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, ref, watch } from 'vue';
import { Badge } from 'ant-design-vue';
import { DownOutlined } from '@ant-design/icons-vue';

import Utils from '@/api/Utils';

import { useModuleStatusesStore } from '@/stores/moduleStatuses';

import { datetimeToString } from '@/components/common/dateUtils';

import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import ModuleStatusItem from '@/components/floaters/ModuleStatusItem.vue';

import type {
	BadgeStatusKey,
	ModuleMessageDisplay,
	ModuleStatusDisplay,
	ModuleStatusKey,
	ModuleStatusRaw,
} from '@/components/floaters/types/moduleStatusesTypes';


const BADGES = {
	unknown: 'default',
	online: 'success',
	warnings: 'warning',
	failed: 'error',
	disabled: 'default',
} satisfies Record<ModuleStatusKey, BadgeStatusKey>;

const IMPORTANT_STATUS_KEYS: readonly string[] = ['caster', 'near', 'nrtk', 'brdcm', 'downloader', 'paas_queue', 'timesync'];
const PROBLEM_STATUSES: ReadonlySet<ModuleStatusKey> = new Set(['warnings', 'failed']);


const sortMessages = (messages: ModuleMessageDisplay[]) => Utils.sortArray(
	messages,
	(a: ModuleMessageDisplay, b: ModuleMessageDisplay) => {
		const levelComp = Utils.sortArrayAccordingToValuesComparator(['ERROR', 'WARNING', 'INFO', 'DEBUG'])(a.level, b.level);
		if (levelComp !== 0) {
			return levelComp;
		}
		const tagComp = String(a.tag).localeCompare(String(b.tag));
		if (tagComp !== 0) {
			return tagComp;
		}
		return a.createdStr.localeCompare(b.createdStr);
	},
);


const addStatusItemData = (item: ModuleStatusRaw): ModuleStatusDisplay => {
	return {
		...item,
		badge: BADGES[item.status],
		messages: sortMessages(item.messages.map(message => ({
			...message,
			createdStr: datetimeToString(message.created),
		}))),
	};
}


export default {
	components: {
		Badge, DownOutlined, DividerAutoDirection, ModuleStatusItem,
	},
	props: {
		isOpen: {
			type: Boolean,
			required: true,
		},
		onClick: {
			type: Function as PropType<() => void>,
			required: true,
		},
	},
	setup(props) {
		const moduleStatusesStore = useModuleStatusesStore();
		const otherStatusesOpen = ref(false);
		const summary = computed<BadgeStatusKey>(() => BADGES[moduleStatusesStore.summary]);
		const moduleStatusesDetails = computed<ModuleStatusDisplay[]>(
			() => moduleStatusesStore.statuses.map(addStatusItemData),
		);
		const importantStatuses = computed(() => {
			const statusesByKey = new Map(moduleStatusesDetails.value.map(status => [status.key, status] as const));
			return IMPORTANT_STATUS_KEYS
				.map(key => statusesByKey.get(key))
				.filter((status): status is ModuleStatusDisplay => status != null);
		});
		const secondaryStatuses = computed(() => moduleStatusesDetails.value.filter(
			status => !IMPORTANT_STATUS_KEYS.includes(status.key),
		));
		const problemStatuses = computed(() => secondaryStatuses.value.filter(status => PROBLEM_STATUSES.has(status.status)));
		const otherStatuses = computed(() => secondaryStatuses.value.filter(status => !PROBLEM_STATUSES.has(status.status)));

		watch(() => props.isOpen, isOpen => {
			if (!isOpen) {
				otherStatusesOpen.value = false;
			}
		});

		return {
			summary,
			moduleStatusesDetails,
			importantStatuses,
			problemStatuses,
			otherStatuses,
			otherStatusesOpen,
		}
	},
}
</script>

<style scoped>
.statuses-floater.nobr {
	overflow: hidden;
}

.other-statuses-toggle {
	display: flex;
	align-items: center;
	gap: 4px;
	width: 100%;
	padding: 0 8px;
	border: 0;
	background: none;
	cursor: pointer;
}

.other-statuses-divider {
	flex: 1;
	min-width: 0;
	margin: 8px 0;
}

.other-statuses-toggle .anticon {
	color: rgba(0, 0, 0, 0.45);
	transition: transform 0.2s;
}

.other-statuses-chevron-open {
	transform: rotate(180deg);
}
</style>

<style>
.statuses-floater .ant-badge-status-dot {
	top: -2px;
	width: 12px;
	height: 12px;
}
</style>
